/**
 * Status sentinel — bridge between trace-mcp server and guard hook.
 *
 * Two files are written, each into two directories:
 *   1. trace-mcp-status-{projectHash}.json — rich JSON status with PID, last
 *      heartbeat, tool-call counters, last successful call timestamp, etc.
 *      Used by the v0.8+ hook for stall detection and by the desktop app for
 *      the project status badge.
 *   2. trace-mcp-alive-{projectHash} — legacy mtime-only sentinel, kept for
 *      backward compatibility with hook v0.7.x.
 *
 * The primary location is {@link STATUS_DIR} under the state home, which every
 * process resolves identically. The `$TMPDIR` copies are written only so a hook
 * installed before TRA-869 keeps working; see STATUS_DIR for why $TMPDIR alone
 * silently broke the channel.
 *
 * The hook treats a missing/stale status as "MCP unavailable" and falls
 * back to allowing Read with a warning instead of hard-blocking. This
 * closes the legitimate fallback case (crashed server, "session not found")
 * without re-introducing the retry-bypass loophole.
 *
 * `transport` records which command produced this sentinel ("stdio" for
 * `serve` / the daemon backing it, "http" for `serve-http`). The guard hook
 * compares it against the calling client's configured transport (from
 * .mcp.json) so a `serve-http` process can't be mistaken for a live `serve`
 * (stdio) session — see GH #297.
 */

import fs from 'node:fs';
import os from 'node:os';
import path from 'node:path';
import { isDangerousProjectRoot } from '../dangerous-root.js';
import { projectHash, STATUS_DIR } from '../global.js';
import { writeTmpFileSync } from '../utils/safe-fs.js';

/** Flush cadence while at least one MCP session is active. */
const FLUSH_INTERVAL_ACTIVE_MS = 5_000;
/** Slower flush cadence while no sessions are active (back-off). */
const FLUSH_INTERVAL_IDLE_MS = 30_000;
const STATUS_SCHEMA_VERSION = 2;

export type ServerTransport = 'stdio' | 'http';

interface StatusState {
  schema: number;
  pid: number;
  transport: ServerTransport;
  started_at: string;
  last_heartbeat_at: string;
  last_successful_tool_call_at: string | null;
  last_failed_tool_call_at: string | null;
  tool_calls_total: number;
  tool_calls_failed: number;
  mcp_sessions_active: number;
}

export interface HeartbeatHandle {
  /** Stop the timer and remove sentinel files. */
  stop(): void;
  /** Record a tool-call result. Updates counters and last-call timestamp. */
  recordToolCall(success: boolean): void;
  /** Update the active-sessions gauge. */
  setSessionsActive(count: number): void;
  /** Force a synchronous flush of the in-memory state to disk. */
  flush(): void;
  /** Snapshot of the current in-memory state (for in-process readers / tests). */
  getState(): Readonly<{
    schema: number;
    pid: number;
    transport: ServerTransport;
    started_at: string;
    last_heartbeat_at: string;
    last_successful_tool_call_at: string | null;
    last_failed_tool_call_at: string | null;
    tool_calls_total: number;
    tool_calls_failed: number;
    mcp_sessions_active: number;
  }>;
  /** Path of the rich status JSON file (for tests). */
  readonly path: string;
  /** Path of the legacy mtime-only sentinel (for tests / v0.7 hook). */
  readonly legacyPath: string;
}

function statusName(projectRoot: string): string {
  return `trace-mcp-status-${projectHash(path.resolve(projectRoot))}.json`;
}

function heartbeatName(projectRoot: string): string {
  return `trace-mcp-alive-${projectHash(path.resolve(projectRoot))}`;
}

function statusPath(projectRoot: string): string {
  return path.join(STATUS_DIR, statusName(projectRoot));
}

function legacyHeartbeatPath(projectRoot: string): string {
  return path.join(STATUS_DIR, heartbeatName(projectRoot));
}

/**
 * `$TMPDIR` copies of the two sentinels, for hooks installed before TRA-869.
 * ponytail: drop this pair — and the dual write below — once no supported
 * release reads $TMPDIR.
 */
function tmpdirSentinelPaths(projectRoot: string): [string, string] {
  return [
    path.join(os.tmpdir(), statusName(projectRoot)),
    path.join(os.tmpdir(), heartbeatName(projectRoot)),
  ];
}

/** Age after which a sentinel left behind by a crashed server is collected. */
const STALE_SENTINEL_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Everything STATUS_DIR holds, and nothing else: the sweep below deletes by
 * this list, so an unrelated file a user dropped in there is never touched.
 * `trace-mcp-consulted-` is a directory, the other three are files.
 */
const SWEPT_SENTINEL_PREFIXES = [
  'trace-mcp-status-',
  'trace-mcp-alive-',
  'trace-mcp-bypass-',
  'trace-mcp-consulted-',
];

/**
 * Drop sentinels from servers that died without running `stop()`.
 *
 * `stop()` unlinks its own files, but a killed process never gets there. In
 * $TMPDIR that never mattered — the OS reaps it — whereas STATUS_DIR lives
 * under the state home and is never cleaned by anyone else, so every crashed
 * server, every project ever opened and every expired bypass would accumulate
 * there forever. Best-effort: a sweep that cannot read the directory is not
 * worth failing a server start over.
 *
 * mtime, not age-since-creation: a live server rewrites its sentinel every 5s
 * and a bypass carries a future mtime while it is in force, so neither can be
 * collected while it still means something.
 */
function sweepStaleSentinels(): void {
  const cutoff = Date.now() - STALE_SENTINEL_MAX_AGE_MS;
  let names: string[];
  try {
    names = fs.readdirSync(STATUS_DIR);
  } catch {
    return;
  }
  for (const name of names) {
    if (!SWEPT_SENTINEL_PREFIXES.some((p) => name.startsWith(p))) continue;
    const full = path.join(STATUS_DIR, name);
    try {
      const st = fs.lstatSync(full);
      if (st.isSymbolicLink() || st.mtimeMs > cutoff) continue;
      if (st.isDirectory()) fs.rmSync(full, { recursive: true, force: true });
      else if (st.isFile()) fs.unlinkSync(full);
    } catch {
      /* vanished under us, or not ours — skip */
    }
  }
}

/**
 * Delete every status sentinel belonging to one project root (TRA-2062).
 *
 * `removeProjectArtifacts` reclaimed the index DB, session DBs, topology and
 * decision rows on project removal but left the `STATUS_DIR` sentinels behind,
 * so every ephemeral agent workdir kept its
 * `trace-mcp-{alive,consulted,status}-<hash>` files forever (187 stale-only
 * hashes on the measured machine, same accumulation class as TRA-2016).
 *
 * Best-effort and idempotent: missing files are skipped, and deleting the
 * sentinel of a root that still has a live server is self-healing — the next
 * `flush()` (every 5–30s) recreates it, while readers treat a missing status
 * as "MCP unavailable" fallback, never a hard error. Symlinks are never
 * followed. The `$TMPDIR` copies are removed too, mirroring `stop()`.
 *
 * Bypass files (`trace-mcp-bypass-*`) are intentionally NOT touched: no writer
 * in this repo names them per project hash, so there is no exact name to
 * target and prefix-scanning would risk an unrelated file.
 *
 * @returns absolute paths actually removed (STATUS_DIR + $TMPDIR copies).
 */
export function deleteStatusSentinelsForRoot(projectRoot: string): string[] {
  const hash = projectHash(path.resolve(projectRoot));
  const names = [
    `trace-mcp-status-${hash}.json`,
    `trace-mcp-alive-${hash}`,
    `trace-mcp-consulted-${hash}`,
  ];
  const removed: string[] = [];
  for (const dir of [STATUS_DIR, os.tmpdir()]) {
    for (const name of names) {
      const full = path.join(dir, name);
      try {
        const st = fs.lstatSync(full);
        if (st.isSymbolicLink()) continue;
        if (st.isDirectory()) fs.rmSync(full, { recursive: true, force: true });
        else if (st.isFile()) fs.unlinkSync(full);
        else continue;
        removed.push(full);
      } catch {
        /* missing, or vanished under us — fine */
      }
    }
  }
  return removed;
}

/**
 * Start writing the status sentinel for the given project.
 * Best-effort: any I/O error is swallowed — status is a hint, never a
 * hard requirement for tool execution.
 */
export function startHeartbeat(
  projectRoot: string,
  transport: ServerTransport = 'stdio',
): HeartbeatHandle {
  const file = statusPath(projectRoot);
  const legacy = legacyHeartbeatPath(projectRoot);
  if (isDangerousProjectRoot(projectRoot) !== null) {
    const startedAt = new Date().toISOString();
    return {
      path: file,
      legacyPath: legacy,
      recordToolCall: () => {},
      setSessionsActive: () => {},
      flush: () => {},
      getState: () => ({
        schema: STATUS_SCHEMA_VERSION,
        pid: process.pid,
        transport,
        started_at: startedAt,
        last_heartbeat_at: startedAt,
        last_successful_tool_call_at: null,
        last_failed_tool_call_at: null,
        tool_calls_total: 0,
        tool_calls_failed: 0,
        mcp_sessions_active: 0,
      }),
      stop: () => {},
    };
  }

  const [tmpFile, tmpLegacy] = tmpdirSentinelPaths(projectRoot);
  try {
    fs.mkdirSync(STATUS_DIR, { recursive: true, mode: 0o700 });
  } catch {
    /* best-effort — flush() below is already failure-tolerant */
  }
  sweepStaleSentinels();
  const startedAt = new Date().toISOString();

  const state: StatusState = {
    schema: STATUS_SCHEMA_VERSION,
    pid: process.pid,
    transport,
    started_at: startedAt,
    last_heartbeat_at: startedAt,
    last_successful_tool_call_at: null,
    last_failed_tool_call_at: null,
    tool_calls_total: 0,
    tool_calls_failed: 0,
    // A stdio server exists only because a client spawned it and holds its
    // pipes — one attached session, by construction. Nothing ever called
    // setSessionsActive(), so this stayed 0 and the guard hook's
    // "mcp_sessions_active == 0 means nobody is attached" check (added in
    // #301) fired on every healthy stdio session, degrading the Read branch to
    // advisory. Measured in TRA-773.
    mcp_sessions_active: transport === 'stdio' ? 1 : 0,
  };

  const flush = () => {
    state.last_heartbeat_at = new Date().toISOString();
    const payload = JSON.stringify(state);
    const stamp = String(Date.now());
    try {
      writeTmpFileSync(file, payload);
      // Touch legacy sentinel for old hook installations.
      writeTmpFileSync(legacy, stamp);
    } catch {
      /* best-effort */
    }
    try {
      writeTmpFileSync(tmpFile, payload);
      writeTmpFileSync(tmpLegacy, stamp);
    } catch {
      /* best-effort — pre-TRA-869 readers only */
    }
  };

  const cadenceFor = (sessionsActive: number): number =>
    sessionsActive > 0 ? FLUSH_INTERVAL_ACTIVE_MS : FLUSH_INTERVAL_IDLE_MS;

  flush();
  let currentIntervalMs = cadenceFor(state.mcp_sessions_active);
  let timer = setInterval(flush, currentIntervalMs);
  if (typeof timer.unref === 'function') timer.unref();

  /**
   * Re-aim the flush interval at the cadence implied by the current session
   * count. No-op when the cadence is unchanged, so it is cheap to call on every
   * session-count change. Reassigns `timer` in this closure so the existing
   * stop()/clearInterval continues to target the live timer.
   */
  const retarget = () => {
    const next = cadenceFor(state.mcp_sessions_active);
    if (next === currentIntervalMs) return;
    currentIntervalMs = next;
    clearInterval(timer);
    timer = setInterval(flush, currentIntervalMs);
    if (typeof timer.unref === 'function') timer.unref();
  };

  return {
    path: file,
    legacyPath: legacy,
    recordToolCall(success: boolean) {
      state.tool_calls_total += 1;
      const now = new Date().toISOString();
      if (success) {
        state.last_successful_tool_call_at = now;
      } else {
        state.tool_calls_failed += 1;
        state.last_failed_tool_call_at = now;
      }
    },
    setSessionsActive(count: number) {
      state.mcp_sessions_active = count;
      // Adjust flush cadence to match active/idle state (no-op if unchanged).
      retarget();
    },
    flush,
    getState() {
      return { ...state };
    },
    stop() {
      clearInterval(timer);
      for (const p of [file, legacy, tmpFile, tmpLegacy]) {
        try {
          fs.unlinkSync(p);
        } catch {
          /* best-effort */
        }
      }
    },
  };
}
